import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  FaBars,
  FaTimes,
  FaUserCircle,
  FaShoppingCart,
  FaSearch
} from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

export default function Navbar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [userInfo, setUserInfo] = useState(null);

  // For desktop dropdowns
  const [catDropdownOpen, setCatDropdownOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);

  // For the search dropdown
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const searchRef = useRef(null);

  // Cart count
  const [cartCount, setCartCount] = useState(0);

  const location = useLocation();
  const navigate = useNavigate();

  // **New**: dynamic logo from homeConfig
  const [logoUrl, setLogoUrl] = useState('/assets/images/ariel-logo.png');

  // Fetch categories + user info + homeConfig
  useEffect(() => {
    // 1) fetch categories
    axios
      .get(`${process.env.REACT_APP_API_URL}/api/categories`)
      .then(res => setCategories(res.data))
      .catch(err => console.error('Error fetching categories:', err));

    // 2) load user from localStorage
    const savedUser = localStorage.getItem('userInfo');
    if (savedUser) {
      setUserInfo(JSON.parse(savedUser));
    }

    // 3) fetch home config for dynamic logo
    axios
      .get(`${process.env.REACT_APP_API_URL}/api/home-config`)
      .then(res => {
        if (res.data.logo) {
          setLogoUrl(`${process.env.REACT_APP_API_URL}/${res.data.logo}`);
        }
      })
      .catch(err => console.error('Error fetching homeConfig:', err));
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // Listen for cart-updated
  useEffect(() => {
    const updateCartCount = () => {
      const cart = JSON.parse(localStorage.getItem('cart')) || [];
      const count = cart.reduce((acc, item) => acc + item.quantity, 0);
      setCartCount(count);
    };
    window.addEventListener('cart-updated', updateCartCount);
    updateCartCount();
    return () => {
      window.removeEventListener('cart-updated', updateCartCount);
    };
  }, []);

  // Toggle search dropdown
  const handleSearchIconClick = () => {
    setIsSearchOpen(!isSearchOpen);
  };

  // Handle search input
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    // No real suggestions -> empty array
    setSuggestions([]);
  };

  // Clicking on a suggestion
  const handleSuggestionClick = (sugName) => {
    setSearchQuery(sugName);
    setSuggestions([]);
    setIsSearchOpen(false);
    navigate(`/shop?search=${encodeURIComponent(sugName)}`);
  };

  // Submit full search
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (!searchQuery) return;
    setIsSearchOpen(false);
    navigate(`/shop?search=${encodeURIComponent(searchQuery)}`);
  };

  // Close search if user clicks outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setIsSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Logout
  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userInfo');
    setUserInfo(null);
    window.location.href = '/';
  };

  return (
    <header className="bg-white shadow fixed w-full top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 flex items-center justify-between h-16">
        {/* Logo (larger height) */}
        <Link to="/">
          <img
            src={logoUrl}
            alt="Aerial Spray Solutions Logo"
            className="h-16 w-auto" 
          />
        </Link>

        {/* Desktop Menu */}
        <nav className="hidden md:flex items-center space-x-6">
          <Link to="/" className="hover:text-primaryGreen font-medium">
            Home
          </Link>

          {/* Categories dropdown */}
          <div className="relative">
            <button
              onClick={() => {
                setProfileDropdownOpen(false);
                setCatDropdownOpen(!catDropdownOpen);
              }}
              className="hover:text-primaryGreen font-medium"
            >
              Categories
            </button>
            {catDropdownOpen && (
              <div className="absolute bg-white shadow-md mt-2 py-2 w-44 z-10 right-0 md:left-0">
                {categories.map(cat => (
                  <Link
                    key={cat._id}
                    to={`/shop?category=${cat.name}`}
                    className="block px-4 py-1 hover:bg-lightGray"
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <Link to="/shop" className="hover:text-primaryGreen font-medium">
            Shop
          </Link>
          <Link to="/blogs" className="hover:text-primaryGreen font-medium">
            Blogs
          </Link>
          <Link to="/about" className="hover:text-primaryGreen font-medium">
            About
          </Link>
          <Link to="/contact" className="hover:text-primaryGreen font-medium">
            Contact
          </Link>

          {/* Auth */}
          {!userInfo ? (
            <>
              <Link to="/login" className="hover:text-primaryGreen font-medium">
                Login
              </Link>
              <Link to="/register" className="hover:text-primaryGreen font-medium">
                Register
              </Link>
            </>
          ) : (
            <div className="relative">
              <button
                onClick={() => {
                  setCatDropdownOpen(false);
                  setProfileDropdownOpen(!profileDropdownOpen);
                }}
                className="flex items-center space-x-1 hover:text-primaryGreen font-medium"
              >
                <FaUserCircle size={20} />
                <span>Profile</span>
              </button>
              {profileDropdownOpen && (
                <div className="absolute bg-white shadow-md mt-2 py-2 w-40 right-0 z-10">
                  <Link
                    to="/user-dashboard"
                    className="block px-4 py-1 hover:bg-lightGray"
                  >
                    My Account
                  </Link>
                  {userInfo.isAdmin && (
                    <Link
                      to="/admin-dashboard"
                      className="block px-4 py-1 hover:bg-lightGray"
                    >
                      Admin Dashboard
                    </Link>
                  )}
                  <button
                    onClick={handleLogout}
                    className="block w-full text-left px-4 py-1 hover:bg-lightGray"
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Search Icon */}
          <div className="relative" ref={searchRef}>
            <button
              onClick={handleSearchIconClick}
              className="hover:text-primaryGreen font-medium"
            >
              <FaSearch size={18} />
            </button>
            {isSearchOpen && (
              <div className="absolute right-0 mt-2 w-64 bg-white p-2 shadow-md z-20">
                <form onSubmit={handleSearchSubmit} className="flex flex-col">
                  <input
                    type="text"
                    className="border p-2 w-full"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    placeholder="Search products/blogs..."
                    autoFocus
                  />
                  {suggestions.length > 0 && (
                    <div className="bg-white border mt-1 max-h-40 overflow-y-auto">
                      {suggestions.map((sug, idx) => (
                        <div
                          key={idx}
                          className="px-2 py-1 hover:bg-lightGray cursor-pointer"
                          onClick={() => handleSuggestionClick(sug.name)}
                        >
                          {sug.name}
                        </div>
                      ))}
                    </div>
                  )}
                  <button
                    type="submit"
                    className="mt-2 bg-primaryGreen text-white py-1 rounded hover:bg-green-500"
                  >
                    Search
                  </button>
                </form>
              </div>
            )}
          </div>

          {/* Cart icon */}
          <Link to="/cart" className="relative hover:text-primaryGreen">
            <FaShoppingCart size={20} />
            {cartCount > 0 && (
              <span className="absolute -top-2 -right-3 bg-secondaryGold text-black 
                             rounded-full text-xs w-5 h-5 flex items-center justify-center">
                {cartCount}
              </span>
            )}
          </Link>
        </nav>

        {/* Mobile Menu Toggle + Search Icon */}
        <div className="md:hidden flex items-center space-x-4">
          {/* Mobile search icon */}
          <div className="relative" ref={searchRef}>
            <button
              onClick={handleSearchIconClick}
              className="text-gray-700 hover:text-primaryGreen"
            >
              <FaSearch size={20} />
            </button>
            {isSearchOpen && (
              <div className="absolute right-0 top-10 w-64 bg-white p-2 shadow-md z-20">
                <form onSubmit={handleSearchSubmit} className="flex flex-col">
                  <input
                    type="text"
                    className="border p-2 w-full"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    placeholder="Search..."
                    autoFocus
                  />
                  {suggestions.length > 0 && (
                    <div className="bg-white border mt-1 max-h-40 overflow-y-auto">
                      {suggestions.map((sug, idx) => (
                        <div
                          key={idx}
                          className="px-2 py-1 hover:bg-lightGray cursor-pointer"
                          onClick={() => handleSuggestionClick(sug.name)}
                        >
                          {sug.name}
                        </div>
                      ))}
                    </div>
                  )}
                  <button
                    type="submit"
                    className="mt-2 bg-primaryGreen text-white py-1 rounded hover:bg-green-500"
                  >
                    Search
                  </button>
                </form>
              </div>
            )}
          </div>

          {/* Mobile menu toggle */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="text-gray-700 hover:text-primaryGreen"
          >
            {isMobileMenuOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu (AnimatePresence) */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden bg-white shadow absolute top-16 left-0 w-full z-50"
          >
            <div className="flex flex-col px-4 py-4 space-y-3">
              <Link
                to="/"
                onClick={() => setIsMobileMenuOpen(false)}
                className="hover:text-primaryGreen"
              >
                Home
              </Link>
              <p className="font-medium text-gray-600">Categories:</p>
              {categories.map(cat => (
                <Link
                  key={cat._id}
                  to={`/shop?category=${cat.name}`}
                  className="hover:text-primaryGreen ml-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {cat.name}
                </Link>
              ))}

              <Link
                to="/shop"
                onClick={() => setIsMobileMenuOpen(false)}
                className="hover:text-primaryGreen"
              >
                Shop
              </Link>
              <Link
                to="/blogs"
                onClick={() => setIsMobileMenuOpen(false)}
                className="hover:text-primaryGreen"
              >
                Blogs
              </Link>
              <Link
                to="/about"
                onClick={() => setIsMobileMenuOpen(false)}
                className="hover:text-primaryGreen"
              >
                About
              </Link>
              <Link
                to="/contact"
                onClick={() => setIsMobileMenuOpen(false)}
                className="hover:text-primaryGreen"
              >
                Contact
              </Link>

              {!userInfo ? (
                <>
                  <Link
                    to="/login"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="hover:text-primaryGreen"
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="hover:text-primaryGreen"
                  >
                    Register
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    to="/user-dashboard"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="hover:text-primaryGreen"
                  >
                    My Account
                  </Link>
                  {userInfo.isAdmin && (
                    <Link
                      to="/admin-dashboard"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="hover:text-primaryGreen"
                    >
                      Admin Dashboard
                    </Link>
                  )}
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMobileMenuOpen(false);
                    }}
                    className="text-left hover:text-primaryGreen"
                  >
                    Logout
                  </button>
                </>
              )}

              {/* Cart LAST */}
              <Link
                to="/cart"
                className="relative hover:text-primaryGreen"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <div className="flex items-center">
                  <FaShoppingCart size={20} />
                  {cartCount > 0 && (
                    <span className="ml-2 bg-secondaryGold text-black 
                                   rounded-full text-xs w-4 h-4 flex items-center justify-center">
                      {cartCount}
                    </span>
                  )}
                  <span className="ml-2">Cart</span>
                </div>
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
